import React, { JSXElementConstructor, ReactElement, ReactNode } from 'react';
import { cx, css } from '@emotion/css';
import cvar from './theme/cvar';
import { PublicComponentProps } from './types';

type Size = 'default' | 'sm' | 'lg';

type Variant = 'default' | 'secondary' | 'primary' | 'link' | 'anchor';

export interface ButtonProps extends PublicComponentProps {
  /**
   * The ability to make the button fill the content block.
   */
  blockLevel?: boolean;

  /**
   * The inner contents of the button.
   */
  children?: ReactNode;

  /**
   * The ability to disable the button.
   */
  disabled?: boolean;

  /**
   * The ability to make the button navigate to a url.
   */
  href?: string;
  /**
   * Specify navigation behavior
   */
  target?: '_blank' | '_parent' | '_self' | '_top';

  /**
   * Icon to display as the button content.
   */
  icon?: ReactNode;

  /**
   * The callback function used when the onMouseEnter event occurs.
   */
  onMouseEnter?: (e: any) => void;

  /**
   * The callback function used when the onMouseLeave event occurs.
   */
  onMouseLeave?: (e: any) => void;

  /**
   * The callback function used when a button is clicked.
   */
  onClick?: (e: any) => void;

  /**
   * The size of the button you are creating.
   */
  size?: Size;

  /**
   * The variant of button you are creating.
   */
  variant?: Variant;
}

export interface ATagProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: ReactNode;
  icon?: ReactNode;
  href?: string;
  target?: string;
  disabled?: boolean;
  size?: string;
  onClick?: any;
  className?: string;
}

export interface ButtonTagProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  size?: string;
  onClick?: any;
  className?: string;
}

const defaultButtonStyle = css`
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  border-radius: 2px;
  border: 1px solid ${cvar('color-button-primary')};
  color: ${cvar('color-button-primary')};
  background: ${cvar('color-button-default')};
  &:hover {
    background: ${cvar('color-button-secondary-hover')};
    border-color: ${cvar('color-button-secondary-hover')};
    color: ${cvar('color-button-primary')};
  }
  &:active {
    background: ${cvar('color-button-secondary-active')};
    border-color: ${cvar('color-button-secondary-active')};
    color: ${cvar('color-button-primary')};
  }
`;

const primaryButtonStyle = css`
  color: ${cvar('color-text-inverse')};
  background: ${cvar('color-interactive')};
  &:hover {
    background: ${cvar('color-button-primary-hover')};
    border-color: ${cvar('color-button-primary-hover')};
    color: ${cvar('color-text-inverse')};
  }
  &:active {
    background: ${cvar('color-button-primary-active')};
    border-color: ${cvar('color-button-primary-active')};
    color: ${cvar('color-text-inverse')};
  }
`;

const linkButtonStyle = css`
  border-color: transparent;
  background: transparent;
  text-decoration: none;
  text-shadow: none;
  &:hover,
  &:active {
    text-decoration: none;
  }
`;

const anchorStyle = css`
  border-color: transparent;
  background: transparent;
  font-weight: 400;
  &:hover,
  &:active {
    background: transparent;
    border-color: transparent;
    text-decoration: underline;
  }
`;

const disabledAnchorStyle = css`
  cursor: text;
`;

const disabledButtonStyle = css`
  background: ${cvar('color-button-disabled')};
  opacity: 0.65;
  color: ${cvar('color-text-disabled-dark')};
  border-color: transparent;
  cursor: not-allowed;
  &:hover {
    background: ${cvar('color-button-disabled')};
    opacity: 0.65;
    color: ${cvar('color-text-disabled-dark')};
    border-color: transparent;
    cursor: not-allowed;
  }
`;

const blockLevelButtonStyle = css`
  width: 100%;
  display: block;
`;

const btnIconSizeCss = (size: Size) => {
  if (size === 'lg')
    return css`
      padding: ${cvar('spacing-16')};
    `;
  if (size === 'sm')
    return css`
      padding: ${cvar('spacing-8')};
    `;
  return css`
    padding: ${cvar('spacing-12')};
  `;
};

const btnSizeCss = (size: Size) => {
  if (size === 'lg')
    return css`
      padding: ${cvar('spacing-12')} ${cvar('spacing-32')};
    `;
  if (size === 'sm')
    return css`
      padding: ${cvar('spacing-4')} ${cvar('spacing-12')};
    `;
  return css`
    padding: ${cvar('spacing-8')} ${cvar('spacing-24')};
  `;
};

const btnFontCss = (size: Size) => {
  if (size === 'lg')
    return css`
      font: ${cvar('text-button-large')};
    `;
  if (size === 'sm')
    return css`
      font: ${cvar('text-button-small')};
    `;
  return css`
    font: ${cvar('text-button-medium')};
  `;
};

const buttonClassTypes = (variant: Variant) => {
  switch (variant) {
    case 'primary':
      return primaryButtonStyle;
    case 'link':
      return linkButtonStyle;
    case 'anchor':
      return anchorStyle;
    default:
      return defaultButtonStyle;
  }
};

const disabledButtonClassTypes = (variant: Variant) => {
  switch (variant) {
    case 'anchor':
      return `${anchorStyle} ${disabledAnchorStyle}`;
    default:
      return disabledButtonStyle;
  }
};

const aTag = ({ children, icon, className, ...rest }: ATagProps) => (
  <a {...rest} className={cx('crc-anchor', className)}>
    {icon || children}
  </a>
);

const buttonTag = ({ children, icon, className, ...rest }: ButtonTagProps) => (
  <button {...rest} className={cx('crc-button', className)}>
    {icon || children}
  </button>
);

export const Button = ({
  blockLevel = false,
  className,
  disabled = false,
  href,
  icon: originalIcon,
  onClick,
  size = 'default',
  variant = 'default',
  target,
  ...rest
}: ButtonProps) => {
  const icon =
    originalIcon &&
    React.cloneElement(originalIcon as ReactElement<any, string | JSXElementConstructor<any>>, {
      style: { display: 'block' },
    });

  const variantCss = cx(
    btnFontCss(size),
    defaultButtonStyle,
    disabled ? disabledButtonClassTypes(variant) : buttonClassTypes(variant),
    variant === 'anchor' ? '' : icon ? btnIconSizeCss(size) : btnSizeCss(size),
    { [blockLevelButtonStyle]: blockLevel },
    className,
  );

  return href
    ? aTag({ href, target, icon, disabled, size, onClick, className: variantCss, ...rest })
    : buttonTag({ disabled, icon, size, onClick, className: variantCss, ...rest });
};
